import {type FC, useCallback, useEffect, useRef} from 'react';
import {useBlocker} from 'react-router';

import type {ThemeColor} from '@yourcoach/shared/styles';

import {useI18n} from '@yourcoach/i18n';
import {alert} from '@yourcoach/shared/components/alert';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';

/**
 * NOTE: be sure that "when" is not depend from any state
 * because it won't change since useEffect has no dependencies
 */
type Props = {
  icon?: FC;
  iconColor?: ThemeColor;
  message: string;
  title?: string;
};

export const useLeaveBlocker = ({icon, iconColor, message, title}: Props) => {
  const {t} = useI18n();
  const shouldBlock = useRef(false);

  const setShouldBlock = (value: boolean) => (shouldBlock.current = value);

  const onBeforeUnload = useCallback(
    async (e: BeforeUnloadEvent) => {
      if (shouldBlock.current) {
        e.preventDefault();

        // Included for legacy support, e.g. Chrome/Edge < 119
        e.returnValue = '';
      }
    },
    [shouldBlock],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [onBeforeUnload]);

  const blocker = useBlocker(() => shouldBlock.current);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      alert.show({
        buttons: [
          {
            label: t('shared:cancel'),
            onClick: () => blocker.reset(),
            scheme: 'gray',
            testID: TestID.fd.crud.alert.lost_changes_warning.button.cancel,
          },
          {
            label: t('shared:ok'),
            onClick: () => blocker.proceed(),
            scheme: 'primary',
            testID: TestID.fd.crud.alert.lost_changes_warning.button.ok,
          },
        ],
        icon,
        iconColor,
        message,
        testID: TestID.fd.crud.alert.lost_changes_warning.container,
        title,
      });
    }
  }, [blocker.state, blocker, icon, iconColor, message, title, t]);

  return {setShouldBlockLeave: setShouldBlock};
};
